import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTerritoryStore } from '../../store/territoryStore';
import StateStatsBar from './StateStatsBar';
import type { StatusFilter } from './StateStatsBar';
import StateMap from './StateMap';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import type { SchoolStatus } from '../../types';

// Module-level ZCTA cache — persists across component mounts
const zctaCache = new Map<string, GeoJSON.FeatureCollection>();

interface StateDetailViewProps {
  stateCode: string;
  onBack: () => void;
}

export default function StateDetailView({ stateCode, onBack }: StateDetailViewProps) {
  const {
    stateDetail,
    isLoadingStateDetail,
    stateDetailError,
    fetchStateDetail,
    clearStateDetail,
  } = useTerritoryStore();

  const [zctaData, setZctaData] = useState<GeoJSON.FeatureCollection | null>(
    zctaCache.get(stateCode) ?? null
  );
  const [zctaLoading, setZctaLoading] = useState(!zctaCache.has(stateCode));

  // School status filter — all visible by default
  const [activeFilters, setActiveFilters] = useState<StatusFilter>(
    new Set<SchoolStatus>(['blast', 'competitor', 'open'])
  );
  const [showTerritories, setShowTerritories] = useState(true);
  const [showHighways, setShowHighways] = useState(false);
  const [showCities, setShowCities] = useState(true);

  const handleToggleFilter = useCallback((status: SchoolStatus) => {
    setActiveFilters(prev => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  }, []);

  const handleShowAll = useCallback(() => {
    setActiveFilters(prev => {
      if (prev.size === 3) return new Set<SchoolStatus>();
      return new Set<SchoolStatus>(['blast', 'competitor', 'open']);
    });
  }, []);

  // Filtered state detail — only include schools matching active filters
  const filteredStateDetail = useMemo(() => {
    if (!stateDetail) return null;
    if (activeFilters.size === 3) return stateDetail; // no filtering needed
    return {
      ...stateDetail,
      schools: stateDetail.schools.filter(s => activeFilters.has(s.status)),
    };
  }, [stateDetail, activeFilters]);

  // Fetch state detail from API
  useEffect(() => {
    fetchStateDetail(stateCode);
    return () => clearStateDetail();
  }, [stateCode, fetchStateDetail, clearStateDetail]);

  // Fetch ZCTA GeoJSON from static file (parallel with API call)
  useEffect(() => {
    if (zctaCache.has(stateCode)) {
      setZctaData(zctaCache.get(stateCode)!);
      setZctaLoading(false);
      return;
    }

    let cancelled = false;
    setZctaLoading(true);

    fetch(`/data/zcta/${stateCode}.json`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data: GeoJSON.FeatureCollection) => {
        if (cancelled) return;
        zctaCache.set(stateCode, data);
        setZctaData(data);
        setZctaLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        // ZCTA 404 is expected for some states — just show dots without polygons
        setZctaLoading(false);
      });

    return () => { cancelled = true; };
  }, [stateCode]);

  // Loading skeleton
  if (isLoadingStateDetail && !stateDetail) {
    return (
      <div>
        {/* Stats bar skeleton */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-4 animate-pulse">
          <div className="flex items-center gap-4">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24" />
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-32" />
          </div>
        </div>
        {/* Map skeleton */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm animate-pulse" style={{ height: '60vh' }}>
          <div className="h-full flex items-center justify-center text-gray-400 dark:text-gray-600">
            Loading state data...
          </div>
        </div>
      </div>
    );
  }

  // API error
  if (stateDetailError) {
    return (
      <div>
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-4">
          <button
            onClick={onBack}
            className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
          >
            &larr; Back to US Map
          </button>
        </div>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
          <AlertTriangle className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-700 dark:text-red-300 mb-3">{stateDetailError}</p>
          <button
            onClick={() => fetchStateDetail(stateCode)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/60 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!stateDetail || !filteredStateDetail) return null;

  return (
    <div>
      <StateStatsBar
        stateName={stateDetail.state.name}
        stats={stateDetail.stats}
        territoryCount={stateDetail.territories.length}
        activeFilters={activeFilters}
        onToggleFilter={handleToggleFilter}
        onShowAll={handleShowAll}
        showTerritories={showTerritories}
        onToggleTerritories={() => setShowTerritories(prev => !prev)}
        showHighways={showHighways}
        onToggleHighways={() => setShowHighways(prev => !prev)}
        showCities={showCities}
        onToggleCities={() => setShowCities(prev => !prev)}
        onBack={onBack}
      />
      <StateMap
        stateCode={stateCode}
        stateDetail={filteredStateDetail}
        zctaData={zctaData}
        showTerritories={showTerritories}
        showHighways={showHighways}
        showCities={showCities}
      />
      {zctaLoading && (
        <div className="text-center text-xs text-gray-400 dark:text-gray-500 mt-2">
          Loading territory boundaries...
        </div>
      )}
    </div>
  );
}
